import { cn } from '@/lib/utils.ts'
import { MENU_SELECT_TOGGLE_DURATION } from '@/lib/constants/animationsSettings.ts'
import { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import useOnSwipe from '@/lib/hooks/useCloseOnDragDown.ts'
import { useOverlayStore } from '@/components/common/propertyPopoverOverlay/overlay/useOverlayStore.ts'
import { useKeyboardInsetCssVar } from '@/lib/hooks/useKeyboardInsetCssVar.ts'

type PropsT = {
  open: boolean
  className?: string
  children?: ReactNode
  handleSwipeDown?: () => void
}

export default function OverlaySelectWrapper({
  open,
  className,
  children,
  handleSwipeDown,
}: PropsT) {
  const { onTouchEnd, onTouchStart, onTouchMove } = useOnSwipe({
    onSwipeDown: handleSwipeDown,
  })
  const swipeDownDisabled = useOverlayStore(state => state.swipeDownDisabled)
  useKeyboardInsetCssVar(open)

  // Portal to document.body to escape any ancestor with a non-default
  // `transform` (route animation wrappers, framer-motion shells, etc.).
  // CSS spec: `position: fixed` becomes relative to the nearest
  // transformed ancestor — so without this portal the bottom-sheet
  // anchors to that ancestor's bottom edge (often 1000+px below the
  // viewport in a long single-issue page) instead of the viewport
  // bottom, leaving the popover off-screen and requiring the user to
  // scroll to see it. Verified live on Galaxy A16: before portal,
  // sheet top=1890 (off-screen); after portal, sheet top=832 (viewport
  // edge, sliding up into view when open).
  return createPortal(
    <div
      // Swipe-to-dismiss lives on the handle zone below so dragging the list
      // stays native scrolling instead of closing the sheet.
      className={cn(
        `fixed bottom-0 left-0 z-[10010] w-full md:w-[var(--sidebar-width)]`,
        open ? `translate-y-0` : `pointer-events-none translate-y-[100%] opacity-0`,
        MENU_SELECT_TOGGLE_DURATION,
        className,
      )}
      style={{ bottom: 'var(--kbd-inset, 0px)' }}
    >
      <div className={`flex justify-center py-2 md:hidden`}>
        <button
          className={cn(
            `h-1.5 w-20 rounded-full border bg-secondary duration-100`,
            swipeDownDisabled ? 'opacity-80' : 'opacity-100',
          )}
        />
      </div>
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={`absolute left-0 right-0 top-0 z-[1909] h-10 md:hidden`}
      ></div>
      {children}
    </div>,
    document.body,
  )
}
